import { useState, useEffect } from 'react';
import { configApi, AppConfig } from '../../../services/configApi';

export interface SectionConfigState {
  originalValues: Partial<AppConfig>;
  currentValues: Partial<AppConfig>;
  hasChanges: boolean;
  saving: boolean;
}

export function useSectionConfig(
  keys: (keyof AppConfig)[],
  parentConfig: AppConfig
) {
  const [state, setState] = useState<SectionConfigState>({
    originalValues: {},
    currentValues: {},
    hasChanges: false,
    saving: false,
  });

  // Update when parent config changes (on initial load or external updates)
  useEffect(() => {
    const sectionValues = keys.reduce((acc, key) => {
      acc[key] = parentConfig[key];
      return acc;
    }, {} as Partial<AppConfig>);

    setState(prev => ({
      ...prev,
      originalValues: { ...sectionValues },
      currentValues: { ...sectionValues },
      hasChanges: false,
    }));
  }, [parentConfig, keys]);

  const updateValue = (key: keyof AppConfig, value: number | boolean | string | null) => {
    setState(prev => {
      const newCurrentValues = { ...prev.currentValues, [key]: value };
      const hasChanges = keys.some(
        k => newCurrentValues[k] !== prev.originalValues[k]
      );

      return {
        ...prev,
        currentValues: newCurrentValues,
        hasChanges,
      };
    });
  };

  const saveChanges = async () => {
    try {
      // Capture current state synchronously before any async operations
      let currentValues: Partial<AppConfig>;
      let originalValues: Partial<AppConfig>;

      setState(prev => {
        currentValues = prev.currentValues;
        originalValues = prev.originalValues;
        return { ...prev, saving: true };
      });

      // Find only the changed values in this section
      const updates = keys
        .filter(key => currentValues![key] !== originalValues![key])
        .map(key => ({ key: key as string, value: currentValues![key] }));

      if (updates.length === 0) {
        setState(prev => ({ ...prev, saving: false }));
        return { success: true, message: 'No changes to save' };
      }

      // Update configurations for this section only
      await configApi.updateMultipleConfigurations(updates);

      // Update original values to match current (saved) values
      setState(prev => ({
        ...prev,
        originalValues: { ...prev.currentValues },
        hasChanges: false,
        saving: false,
      }));

      return { success: true, message: 'Section saved successfully' };
    } catch (error) {
      setState(prev => ({ ...prev, saving: false }));
      return {
        success: false,
        message:
          error instanceof Error ? error.message : 'Failed to save section',
      };
    }
  };

  const discardChanges = () => {
    setState(prev => ({
      ...prev,
      currentValues: { ...prev.originalValues },
      hasChanges: false,
    }));
  };

  return {
    ...state,
    updateValue,
    saveChanges,
    discardChanges,
  };
}
